// 帳號 VMS 同步回放（MV.1，docs/mycam/mv-spec.md ①）：一台攝影機的合併時間軸。
// 規則照 Kinio（plan/SPEC.md:5992-6047）：每一刻只顯示一個來源、使用者不選；優先序 雲端 → 本機（SD / 手機本機）。
// 缺口分種類、不靜默跳：gap（沒錄影）/ offline（設備離線，本機查不到）/ unknown（本機還沒回）/ expired（超過保存期）。
// 「設備離線 ≠ 沒錄影」：本機查不到的時段標 offline / unknown，不當成 gap。

export type SpanKind = 'recorded' | 'gap' | 'offline' | 'unknown' | 'expired';
export type SpanSource = 'cloud' | 'local';

export interface Range {
  fromUs: number;
  toUs: number;
}

export interface Span extends Range {
  kind: SpanKind;
  source?: SpanSource;
}

export interface TimelineInput {
  window: Range;
  cloud: Range[];
  local: Range[];
  /** 本機錄影查詢的狀態：ok = 查到了（local 是完整的）；pending = 還在查；unreachable = 查不到（設備離線 / 連不上） */
  localState: 'ok' | 'pending' | 'unreachable' | 'none';
  /** 比這更早的錄影已過保存期（0 = 不知道） */
  retentionFromUs?: number;
}

/** 片段相隔 ≤ joinUs（預設 2 s）就接起來（Kinio：雲端片段時間外取整、≤ 2 s 接起）。 */
export function mergeRanges(rs: Range[], joinUs = 2_000_000): Range[] {
  const s = rs.filter((r) => r.toUs > r.fromUs).sort((a, b) => a.fromUs - b.fromUs);
  const out: Range[] = [];
  for (const r of s) {
    const last = out[out.length - 1];
    if (last && r.fromUs - last.toUs <= joinUs) last.toUs = Math.max(last.toUs, r.toUs);
    else out.push({ ...r });
  }
  return out;
}

function clip(rs: Range[], w: Range): Range[] {
  return rs.map((r) => ({ fromUs: Math.max(r.fromUs, w.fromUs), toUs: Math.min(r.toUs, w.toUs) })).filter((r) => r.toUs > r.fromUs);
}

/** 合併成連續、不重疊、蓋滿 window 的區段。 */
export function buildTimeline(inp: TimelineInput): Span[] {
  const w = inp.window;
  const cloud = clip(mergeRanges(inp.cloud), w);
  const local = clip(mergeRanges(inp.local), w);
  // 所有邊界點
  const cuts = new Set<number>([w.fromUs, w.toUs]);
  for (const r of [...cloud, ...local]) { cuts.add(r.fromUs); cuts.add(r.toUs); }
  if (inp.retentionFromUs && inp.retentionFromUs > w.fromUs && inp.retentionFromUs < w.toUs) cuts.add(inp.retentionFromUs);
  const pts = [...cuts].sort((a, b) => a - b);
  const inAny = (rs: Range[], t: number) => rs.some((r) => r.fromUs <= t && t < r.toUs);
  const out: Span[] = [];
  for (let i = 0; i + 1 < pts.length; i++) {
    const fromUs = pts[i], toUs = pts[i + 1], mid = fromUs + (toUs - fromUs) / 2;
    let s: Span;
    if (inAny(cloud, mid)) s = { fromUs, toUs, kind: 'recorded', source: 'cloud' };
    else if (inAny(local, mid)) s = { fromUs, toUs, kind: 'recorded', source: 'local' };
    else if (inp.retentionFromUs && mid < inp.retentionFromUs) s = { fromUs, toUs, kind: 'expired' };
    else if (inp.localState === 'pending') s = { fromUs, toUs, kind: 'unknown' };
    else if (inp.localState === 'unreachable') s = { fromUs, toUs, kind: 'offline' };
    else s = { fromUs, toUs, kind: 'gap' };
    const last = out[out.length - 1];
    if (last && last.kind === s.kind && last.source === s.source && last.toUs === s.fromUs) last.toUs = s.toUs;
    else out.push(s);
  }
  return out;
}

/** t 這一刻的區段（找不到 = undefined）。 */
export function spanAt(spans: Span[], tUs: number): Span | undefined {
  return spans.find((s) => s.fromUs <= tUs && tUs < s.toUs);
}

/** 點時間軸空白處：吸到 maxSnapUs 內最近一段有錄影的開頭（Kinio view.ts snapToSpan）；沒有就原地。 */
export function snapToRecorded(spans: Span[], tUs: number, maxSnapUs = 10 * 60_000_000): number {
  const here = spanAt(spans, tUs);
  if (here?.kind === 'recorded') return tUs;
  const next = spans.find((s) => s.kind === 'recorded' && s.fromUs >= tUs);
  return next && next.fromUs - tUs <= maxSnapUs ? next.fromUs : tUs;
}

/** 每台在 t 都沒有可播的錄影（全部是空白 / 離線 / 未知 / 過期）。沒有任何一台 = true。 */
export function allNotRecorded(perCam: Span[][], tUs: number): boolean {
  return perCam.every((spans) => spanAt(spans, tUs)?.kind !== 'recorded');
}

/** t 之後最早的「有錄影」起點（跨所有台）；沒有 = null。 */
export function nextRecordedStart(perCam: Span[][], tUs: number): number | null {
  let best: number | null = null;
  for (const spans of perCam) {
    const s = spans.find((x) => x.kind === 'recorded' && x.fromUs > tUs);
    if (s && (best === null || s.fromUs < best)) best = s.fromUs;
  }
  return best;
}

/** 所有台最早的「有錄影」起點；沒有 = null。 */
export function firstRecordedStart(perCam: Span[][]): number | null {
  return nextRecordedStart(perCam, -Infinity);
}
